import React, { useState } from "react";
import { Pagination, Modal, Alert } from "antd";
import {
  TableData,
  useDeleteTable,
  useTables,
  useBulkDeleteTables,
} from "../hooks/useTables";
import TableHeader from "../components/TableManagement/TableHeader";
import TableComponent from "../components/TableManagement/Table";
import AddTableModal from "../components/TableManagement/AddTableModal";
import QRCodeModal from "../components/TableManagement/QRCodeModal";
import dayjs from "dayjs";
import { usePermissions } from "../context/PermissionContext";
import { PERMISSIONS } from "../utils/permissions";
import { useAuth } from "../hooks/useAuth";

const TableManagementPage: React.FC = () => {
  const { data: user } = useAuth();
  const locationId = user?.location?.id;

  // Get permissions
  const { hasPermission } = usePermissions();
  const canViewTables = hasPermission(PERMISSIONS.VIEW_TABLES);
  const canAddTable = hasPermission(PERMISSIONS.ADD_TABLES);
  const canModifyTable = hasPermission(PERMISSIONS.MODIFY_TABLES);
  const canDeleteTable = hasPermission(PERMISSIONS.DELETE_TABLES);
  const canSeeAllLocations = hasPermission(PERMISSIONS.CAN_SEE_ALL_LOCATIONS);

  // Initialize filters - only include location_id if user can't see all locations
  const [filters, setFilters] = useState<Record<string, any>>(() => {
    const initialFilters: Record<string, any> = {};
    if (!canSeeAllLocations && locationId) {
      initialFilters.location_id = locationId;
    }
    return initialFilters;
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTable, setSelectedTable] = useState<TableData | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isQRCodeModalVisible, setIsQRCodeModalVisible] = useState(false);

  const { data: tablesData, isLoading } = useTables(currentPage, filters);
  const { mutate: deleteTable } = useDeleteTable();
  const { mutate: bulkDeleteTables } = useBulkDeleteTables();

  // Show the AddTableModal
  const handleAddTable = () => {
    if (!canAddTable) {
      Modal.error({
        title: "Permission Denied",
        content: "You don't have permission to add tables.",
      });
      return;
    }
    setIsModalVisible(true);
  };

  // Hide the AddTableModal
  const handleCloseAddModal = () => {
    setIsModalVisible(false);
  };

  // Show the QR Code Modal
  const handleViewQRCode = (table: TableData) => {
    setSelectedTable(table);
    setIsQRCodeModalVisible(true);
  };

  // Hide the QR Code Modal
  const handleCloseQRCodeModal = () => {
    setIsQRCodeModalVisible(false);
    setSelectedTable(null);
  };

  const handleDeleteTable = (tableId: number) => {
    if (!canDeleteTable) {
      Modal.error({
        title: "Permission Denied",
        content: "You don't have permission to delete tables.",
      });
      return;
    }

    Modal.confirm({
      title: "Delete Table",
      content: "Are you sure you want to delete this table?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => deleteTable(tableId),
    });
  };

  const handleBulkDeleteTables = (tableIds: number[]) => {
    if (!canDeleteTable) {
      Modal.error({
        title: "Permission Denied",
        content: "You don't have permission to delete tables.",
      });
      return;
    }

    bulkDeleteTables(tableIds);
  };

  const handleApplyFilters = (newFilters: Record<string, any>) => {
    // Format date range values for API
    const formattedFilters = { ...newFilters };

    // Handle date range
    if (
      formattedFilters.date_range &&
      Array.isArray(formattedFilters.date_range) &&
      formattedFilters.date_range.length === 2
    ) {
      const [startDate, endDate] = formattedFilters.date_range;
      formattedFilters.start_date = dayjs(startDate).format("YYYY-MM-DD");
      formattedFilters.end_date = dayjs(endDate).format("YYYY-MM-DD");

      // Remove the original date_range property
      delete formattedFilters.date_range;
    }

    // Ensure location filter is maintained for users who can't see all locations
    if (!canSeeAllLocations && locationId) {
      formattedFilters.location_id = locationId;
    }

    setFilters(formattedFilters);
    setCurrentPage(1); // Reset to first page when applying filters
  };

  const handleClearFilters = () => {
    const clearedFilters: Record<string, any> = {};
    if (!canSeeAllLocations && locationId) {
      clearedFilters.location_id = locationId;
    }
    setFilters(clearedFilters);
    setCurrentPage(1); // Reset to first page when clearing filters
  };

  // If user doesn't have permission to view tables, show an error message
  if (!canViewTables) {
    return (
      <div className="p-6">
        <Alert
          message="Access Denied"
          description="You don't have permission to view tables. Please contact your administrator if you need access."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="p-6">
      <TableHeader
        onAddTable={handleAddTable}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        canAddTable={canAddTable}
        canSeeAllLocations={canSeeAllLocations}
      />

      <TableComponent
        data={tablesData?.data || []}
        onViewQRCode={handleViewQRCode}
        onDeleteTable={handleDeleteTable}
        onBulkDeleteTables={handleBulkDeleteTables}
        isLoading={isLoading}
        canModifyTable={canModifyTable}
        canDeleteTable={canDeleteTable}
      />

      <div className="flex justify-end mt-4">
        <Pagination
          current={currentPage}
          total={tablesData?.meta.total || 0}
          onChange={(page) => setCurrentPage(page)}
          showSizeChanger={false}
        />
      </div>

      {/* Add Table Modal */}
      <AddTableModal visible={isModalVisible} onCancel={handleCloseAddModal} />

      {/* QR Code Modal */}
      <QRCodeModal
        visible={isQRCodeModalVisible}
        onClose={handleCloseQRCodeModal}
        table={selectedTable || undefined}
        onDelete={canDeleteTable ? handleDeleteTable : undefined}
      />
    </div>
  );
};

export default TableManagementPage;
